import {
  Entity,
  parseEntityRef,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { Table, TableColumn, Progress } from '@backstage/core-components';
import {
  EntityRefLink,
  EntityRefLinks,
  getEntityRelations,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { AwsAccountFilters } from './filters';

const AWS_ACCOUNT_ID = 'temabit.com/aws-account-id';
const AWS_ACCOUNT_STATUS = 'temabit.com/aws-account-status';
const AWS_ACCOUNT_ALIAS = 'temabit.com/aws-account-alias';
const BUSINESS_UNIT = 'temabit.com/business-unit';
const PRM_CODE = 'temabit.com/prm-code';

interface Row {
  entity: Entity;
  id: string;
  alias: string;
  status: string;
  lifecycle: string;
  businessUnit: string;
  prmCode: string;
}

const ann = (entity: Entity, key: string): string =>
  entity.metadata.annotations?.[key] ?? '';

const accountId = (entity: Entity): string =>
  ann(entity, AWS_ACCOUNT_ID) ||
  entity.metadata.name.replace(/^aws-account-/, '');

const toRow = (entity: Entity): Row => ({
  entity,
  id: accountId(entity),
  alias: ann(entity, AWS_ACCOUNT_ALIAS) || entity.metadata.title || '—',
  status: ann(entity, AWS_ACCOUNT_STATUS) || '—',
  lifecycle: (entity.spec as { lifecycle?: string } | undefined)?.lifecycle ?? '—',
  businessUnit: ann(entity, BUSINESS_UNIT) || '—',
  prmCode: ann(entity, PRM_CODE) || '—',
});

const columns: TableColumn<Row>[] = [
  { title: 'Account ID', field: 'id' },
  {
    title: 'Alias',
    field: 'alias',
    render: row => (
      <EntityRefLink entityRef={row.entity} title={row.alias} />
    ),
  },
  { title: 'Status', field: 'status' },
  { title: 'Env', field: 'lifecycle' },
  {
    title: 'Owner',
    field: 'entity.spec.owner',
    render: row => {
      const owner = (row.entity.spec as { owner?: string } | undefined)?.owner;
      if (!owner) return <>—</>;
      // spec.owner is often a BARE ref (e.g. `guests`, `team-a`) — parse with a
      // default kind/namespace so EntityRefLink doesn't throw on a missing kind.
      return (
        <EntityRefLink
          entityRef={parseEntityRef(owner, {
            defaultKind: 'group',
            defaultNamespace: 'default',
          })}
        />
      );
    },
  },
  {
    title: 'System(s)',
    sorting: false,
    render: row => {
      // The account's Systems: partOf (primary) + dependencyOf (shared).
      const systems = [
        ...getEntityRelations(row.entity, RELATION_PART_OF, { kind: 'system' }),
        ...getEntityRelations(row.entity, 'dependencyOf', { kind: 'system' }),
      ];
      return systems.length ? (
        <EntityRefLinks entityRefs={systems} />
      ) : (
        <>—</>
      );
    },
  },
  { title: 'Business unit', field: 'businessUnit' },
  { title: 'PRM code', field: 'prmCode' },
];

/**
 * The D1 results table. Reads the filtered `aws-account` Resources from the
 * entity-list context and renders the account-specific columns the generic
 * catalog table lacks.
 */
export const AwsAccountsTable = () => {
  const { entities, loading, error } = useEntityList<AwsAccountFilters>();

  if (loading) return <Progress />;

  const rows = entities.map(toRow);
  return (
    <Table<Row>
      title={`AWS Accounts (${rows.length})`}
      options={{ paging: true, pageSize: 20, search: true }}
      columns={columns}
      data={rows}
      isLoading={loading}
      emptyContent={
        <div style={{ padding: 16 }}>
          {error
            ? `Failed to load AWS accounts: ${error.message}`
            : 'No AWS accounts match the current filters.'}
        </div>
      }
    />
  );
};
